import { dirs } from '@/helpers/config';

const SCROLL_LOCK_CLASS = 'state-scroll-locked';

export const icon = function (icon) {
	return `
		<svg class="icon icon_${icon}">
			<use href="${dirs.STATIC_ICONS_PATH}#${icon}" />
		</svg>
	`;
};

export const singletonFactory = function (createInstanceFn) {
	let instance = null;

	return {
		getInstance() {
			if (!instance) {
				instance = createInstanceFn();
			}

			return instance;
		}
	};
};

export const mod = (a, b) => ((a % b) + b) % b;

export const getScrollbarWidth = () => {
	const outer = document.createElement('div');

	outer.style.visibility = 'hidden';
	outer.style.overflow = 'scroll';
	outer.style.msOverflowStyle = 'scrollbar';
	document.body.appendChild(outer);

	const inner = document.createElement('div');

	outer.appendChild(inner);

	const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

	outer.parentNode.removeChild(outer);

	return scrollbarWidth;
};

export const appendScriptToBody = (src) => {
	let curTag = document.querySelector(`script[src*="${src}"]`);

	if (curTag && curTag.dataset.loaded) return Promise.resolve(curTag);

	return new Promise((resolve, reject) => {
		let tag = curTag || document.createElement('script');

		tag.addEventListener('load', () => resolve(tag), {
			once: true
		});
		tag.addEventListener('error', () => reject('Error loading script'), {
			once: true
		});

		if (curTag) return;

		tag.addEventListener(
			'load',
			() => {
				tag.dataset.loaded = true;
			},
			{
				once: true
			}
		);

		tag.setAttribute('src', src);
		tag.setAttribute('defer', 'defer');

		if (!curTag) {
			document.body.append(tag);
		}
	});
};

export const onWindowLoaded = (cb) => {
	if (typeof cb !== 'function') return;

	if (document.readyState === 'complete') return cb(window);

	window.addEventListener('load', cb, {
		once: true
	});
};

/**
 * @param {Element} target
 * @param {string|string[]} transitionedProperties
 * @param {Function} cb
 * @param {boolean} [once=true]
 */
export const afterTransition = (target, transitionedProperties, cb, once = true) => {
	const propertiesList = Array.isArray(transitionedProperties)
		? transitionedProperties
		: [transitionedProperties];
	const callback = ({ propertyName }) => {
		if (!propertiesList.includes(propertyName)) return;

		typeof cb === 'function' && cb();

		if (once) {
			target.removeEventListener('transitionend', callback);
		}
	};

	target.addEventListener('transitionend', callback);

	return {
		resolve() {
			typeof cb === 'function' && cb();

			if (once) {
				target.removeEventListener('transitionend', callback);
			}
		},
		destroy() {
			target.removeEventListener('transitionend', callback);
		}
	};
};

/**
 * Locks or unlocks the page scroll while preserving scroll position and preventing layout shift.
 * @param {boolean} [lock=true]
 */
export function setHtmlStateLock(lock = true) {
	const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
	const hasPageScrollbar =
		document.documentElement.scrollHeight > document.documentElement.clientHeight;

	if (lock) {
		if (scrollbarWidth > 0 && hasPageScrollbar) {
			document.documentElement.style.scrollbarGutter = 'stable';
		}

		const scrollY = window.scrollY;

		document.body.dataset.scrollY = scrollY.toString();
		document.body.style.top = `-${scrollY}px`;
		document.documentElement.classList.add(SCROLL_LOCK_CLASS);
	} else {
		if (document.body.dataset.scrollY === undefined) return;

		const scrollY = parseInt(document.body.dataset.scrollY || '0', 10);

		document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
		document.documentElement.style.scrollbarGutter = '';
		document.body.style.top = '';
		delete document.body.dataset.scrollY;

		window.scrollTo({ top: scrollY, behavior: 'auto' });
	}
}

export const isDevMode = () => {
	try {
		return process.env.NODE_ENV === 'development';
	} catch {
		return false;
	}
};

/**
 * Convert various element types to an array.
 * @param {*} elements
 * @returns {Element[]}
 */
export const getElementsArray = (elements) => {
	if (typeof elements === 'string') return [...document.querySelectorAll(elements)];
	if (NodeList.prototype.isPrototypeOf(elements)) return [...elements];
	if (Element.prototype.isPrototypeOf(elements)) return [elements];

	if (Array.isArray(elements)) {
		const cleanArray = elements
			.map((el) => {
				if (Element.prototype.isPrototypeOf(el)) return el;
				if (typeof el === 'string') return document.querySelector(el);
				return null;
			})
			.filter(Boolean);

		return [...new Set(cleanArray)];
	}

	return [];
};

/**
 * Get a single DOM element from a selector.
 * @param {*} element
 * @returns {?Element}
 */
export const getElement = (element) => {
	if (Element.prototype.isPrototypeOf(element)) return element;
	if (typeof element === 'string') return document.querySelector(element);
	if (NodeList.prototype.isPrototypeOf(element) && element.length) return element[0];
	if (Array.isArray(element) && element.length) {
		const firstElement = element[0];

		if (Element.prototype.isPrototypeOf(firstElement)) return firstElement;
		if (typeof firstElement === 'string') return document.querySelector(firstElement);
	}
	return null;
};

/**
 * Selector for interactive, keyboard-focusable elements.
 * @constant {string}
 */
export const FOCUSABLE_ELEMENTS_SELECTOR =
	'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

/**
 * Toggles a class on the <html> element.
 * @param {string|null} className
 * @param {boolean} shouldAdd
 */
export function toggleHtmlClass(className, shouldAdd) {
	if (!className) return;
	document.documentElement.classList.toggle(className, shouldAdd);
}

/**
 * Checks if the user prefers reduced motion.
 * @returns {boolean}
 */
export const getMotionPreference = () => {
	const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

	return mediaQuery.matches;
};

/**
 * Checks if the user prefers reduced data usage (Save Data mode).
 * @returns {boolean}
 */
export const getDataPreference = () => {
	if (window.matchMedia('(prefers-reduced-data: reduce)').matches) return true;

	if (navigator.connection && navigator.connection.saveData === true) return true;

	return false;
};

export const onDomReady = () => {
	return new Promise((resolve, reject) => {
		// front end
		if (!window?.wp?.data) return resolve();

		let blockEditorReady = false;

		// back end
		window.wp.data.subscribe(() => {
			if (blockEditorReady) return;

			const coreEditor = wp.data.select('core/editor');

			let editorReady = coreEditor.canInsertBlockType('core/paragraph');

			if (editorReady) {
				blockEditorReady = true;

				setTimeout(resolve, 1000);
			}
		});
	});
};
